import axios from "axios";
import { getAccessToken, forceLogout, refreshAccessToken } from "../utils/auth";
import { BASE_URL } from "./constants";

// Create axios instance with the appropriate base URL
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to handle authentication
api.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Add response interceptor to handle token expiry and automatic refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if it's a 401 error and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the access token
        const refreshSuccess = await refreshAccessToken();

        if (refreshSuccess) {
          // Retry the original request with the new access token
          const newAccessToken = getAccessToken();
          if (newAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        if (process.env.NODE_ENV === "development") {
          console.error("SuperadminFE: Token refresh failed:", refreshError);
        }
      }

      // If refresh failed or no token, force logout
      forceLogout().catch((logoutError) => {
        if (process.env.NODE_ENV === "development") {
          console.error("SuperadminFE: Error during forceLogout:", logoutError);
        }
      });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

interface GenericResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  status?: number;
}

export interface DocRecord {
  docId: string;
  title: string;
  body: string;
  tags: string[];
  createdOn?: string;
  modifiedOn?: string;
}

export interface DocPayload {
  title: string;
  body: string;
  tags: string[];
}

export interface AgentRecord {
  agentId: string;
  agentKey: string;
  name: string;
  description?: string | null;
  prompt: string;
  enabled: boolean;
  payloadTemplate?: any;
  tags: string[];
  allowedFields?: string[] | null;
  lockedFields?: string[] | null;
  page?: string | null;
  prefetchEndpoint?: string | null;
  actionEndpoint?: string | null;
  actionMethod?: string | null;
  confirmLabel?: string | null;
  successMarkdown?: string | null;
  createdOn?: string;
  modifiedOn?: string;
}

export interface AgentPayload {
  agentId?: string;
  agentKey: string;
  name: string;
  description?: string;
  prompt: string;
  enabled?: boolean;
  payloadTemplate?: any;
  tags?: string[];
  allowedFields?: string[];
  lockedFields?: string[];
  page?: string;
  prefetchEndpoint?: string;
  actionEndpoint?: string;
  actionMethod?: string;
  confirmLabel?: string;
  successMarkdown?: string;
}

export const fetchDocs = async (): Promise<DocRecord[]> => {
  const response = await api.get<GenericResponse<DocRecord[]>>("/ai/get-docs");
  const docs = response.data.data ?? [];
  return docs.map((doc) => ({
    ...doc,
    tags: doc.tags ?? [],
  }));
};

export const createDoc = async (payload: DocPayload): Promise<DocRecord> => {
  const response = await api.post<GenericResponse<DocRecord>>("/ai/docs", payload);
  const doc = response.data.data;
  return {
    ...doc,
    tags: doc?.tags ?? [],
  } as DocRecord;
};

export const updateDoc = async (docId: string, payload: DocPayload): Promise<DocRecord> => {
  const response = await api.put<GenericResponse<DocRecord>>(`/ai/docs/${docId}`, payload);
  const doc = response.data.data;
  return {
    ...doc,
    tags: doc?.tags ?? [],
  } as DocRecord;
};

export const deleteDoc = async (docId: string): Promise<void> => {
  await api.delete(`/ai/delete-doc/${docId}`);
};

export const fetchAgents = async (): Promise<AgentRecord[]> => {
  const response = await api.get<GenericResponse<AgentRecord[]>>("/ai/get-agents");
  const items = response.data.data ?? [];
  return items.map((agent) => ({
    ...agent,
    tags: agent.tags ?? [],
    allowedFields: agent.allowedFields ?? [],
    lockedFields: agent.lockedFields ?? [],
    page: agent.page ?? undefined,
  }));
};

export const saveAgent = async (payload: AgentPayload): Promise<AgentRecord> => {
  const response = await api.post<GenericResponse<AgentRecord>>("/ai/create-agent", payload);
  return response.data.data;
};

export const deleteAgent = async (agentId: string): Promise<void> => {
  await api.delete(`/ai/delete-agent/${agentId}`);
};

export { api, BASE_URL };
